import React, { useEffect, useRef, useState, JSX } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import StyledLink from '@makeship/core/lib/components/Link';
import posthog from 'posthog-js';
import { ButtonPrimary } from '../../../../ui/atoms/Button';
import { ProductStage, ProductTag, SortOrder, SubscribeModalLocation } from '../../../../types/common';
import {
  getStage,
  parseMetafields,
  hasProductTag,
  updateFundedBar,
  isProductSoldOut,
  getProductType,
  calculateTotalInventorySold,
  calculateCombinedMOQ,
  shouldHideProgressBarForProducts,
  getProductIDFromShopifyGID,
  isSizedApparelProduct,
  shouldShowLowStockMessage,
} from '../../../../utils/product';
import {
  createEvergreenTags,
  createTags,
  getSubscribeButtonText,
  getSubscribeDescription,
  getSubscribeTitle,
  isHybridPreOrder,
  isHybridSoldOut,
  renderShippingDate,
  shouldRenderShippingInfo,
} from '../commonFunctions';
import { useFetchSalesQuantityForProductDrop } from '../../../../hooks/useFetchSalesQuantityForProductDrop';
import CompleteCollectionComponent from '../CompleteCollectionComponent';
import { getProductDetailsFromContext, getProductFromContext } from '../../../../context/product';
import ProductDetails from '../ProductDetails';
import ProgressBarContainer from '../ProgressBarContainer';
import PromoBanner from '../PromoBanner';
import AddToCartContainer from '../AddToCartContainer';
import RefundBlock from '../RefundBlock';
import {
  getIsCompleteTheCollectionEligible,
  parseCreatorCollectionMetafields,
  areAllProductDropMetafieldsFilled,
  isGorillaTagDropCollection,
} from '../../../../utils/collections';
import { getBadgesByExternalProductId } from '../../../../api/storefront/badge';
import { decryptShopifyId } from '../../../../utils/id';
import ProductHeader from '../ProductHeader';
import EvergreenBanner from '../EvergreenBanner';
import { isUserLoggedIn, trackSubscribeButtonClicked } from '../../../../utils/analytics';
import GiftingComponent from '../GiftingComponent';
import PDPInfoDrawer from '../PDPInfoDrawer';
import CreatorMessage from '../CreatorMessage';
import { removeFirstProductImage } from '../../../../utils/hoodies';
import LiveProductRedirect from '../LiveProductRedirect';
import LiveProductRedirectFloating from '../LiveProductRedirectFloating';
import { useStore } from '../../../../store';
import config from '../../../../../config.json';
import PastPetitionMessage from '../PastPetitionMessage';
import { P2, S2, S2Styles, S3 } from '../../../Typography';
import { VariantSelectionError } from '../../../../types/errors';
import StretchGoalsRewardList from '../../CustomCreatorCollection/StretchGoalsRewardList';
import StretchGoalsProgressBar from '../../CustomCreatorCollection/StretchGoalsProgressBar';
import SizeGuideModal from '../../../Modal/SizeGuideModal';

const LazySubscribeModal = dynamic(() => import('../../../Modal/SubscribeModal'), { ssr: false });

const ProductInfoWrapper = styled.div`
  width: 100%;
  display: block;
`;

const ProductHeaderWrapper = styled.div`
  width: 100%;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: none;
  }
`;

const PinAddOnBanner = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  border-radius: 8px;
  border: 1px solid rgba(83, 73, 237, 0.33);
  padding: 12px 10px;
`;

const GiftIcon = styled(S3)`
  font-size: 35px;
  line-height: 60px;
  margin-right: 10px;
`;

const ImageWrapper = styled.div`
  position: relative;
  margin-right: 10px;
  min-width: 54px;
  min-height: 54px;
`;

const StretchGoalWrapper = styled.div`
  margin-top: 28px;
  margin-bottom: 2px;
`;

const StretchGoalsProgressBarWrapper = styled.div`
  margin-bottom: 24px;
`;

const PostPurchaseDetailsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const SizeGuide = styled(StyledLink.Primary)`
  ${S2Styles}
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const GTCodeRedemptionContainer = styled.div`
  padding: 10px 16px;
  margin-top: 24px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding: 10px 0;
  }
`;

const LowStockMessage = styled(S2)`
  color: ${({ theme }) => theme.colors.error};
  margin-top: 16px;
`;

type ProductInfoProps = {
  setProductImages: React.Dispatch<React.SetStateAction<Shopify.ImageEdge[]>>;
  selectedVariant?: Shopify.ProductVariant;
  setSelectedVariant: (variant: Shopify.ProductVariant | undefined) => void;
  makeshiftCollectionTitle: string | null;
  productsBySameCreator: Shopify.ProductEdge[];
  productsInSameMakeshiftCollection: Shopify.ProductEdge[];
  giftProduct?: Shopify.Product | null;
  pinAddOnProduct?: Shopify.Product | null;
  topCampaigns: Shopify.ProductEdge[];
  creatorCollection: Shopify.CollectionEdge | null;
  latestProductDropCollection: Shopify.Collection | null;
  productDropCollectionMetafields: ProductDropCollectionMetafields | null;
  shouldEnableStretchGoals: boolean;
  currentProductDropSalesQuantity: number;
};

const ProductInfo: React.FC<ProductInfoProps> = ({
  setProductImages,
  selectedVariant,
  setSelectedVariant,
  makeshiftCollectionTitle,
  productsBySameCreator,
  productsInSameMakeshiftCollection,
  giftProduct,
  pinAddOnProduct,
  topCampaigns,
  creatorCollection,
  latestProductDropCollection,
  productDropCollectionMetafields,
  shouldEnableStretchGoals,
  currentProductDropSalesQuantity,
}: ProductInfoProps) => {
  const product = getProductFromContext();
  const productDetails = getProductDetailsFromContext();
  const creatorCollectionHandle = creatorCollection?.node.handle ?? null;

  const { moq, totalInventory, setTotalInventory, setMOQ } = productDetails;
  const moqNumber = Number(moq);
  const totalInventoryNumber = Number(totalInventory);

  const metafields = parseMetafields(product.metafields);
  const stage = getStage(product.tags);

  const [badges, setBadges] = useState<Badge[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);

  const fundedBarRef = useRef<HTMLDivElement | null>(null);
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);
  const isProductHybridPreOrder = isHybridPreOrder(product);
  const isSoldOut = isProductSoldOut(product, totalInventoryNumber);
  const hasManyVariants = product.variants.edges.length > 1;
  const hasProductionDesign = product.variants.edges.length === 2;
  const isPetitionCampaign = hasProductTag(product.tags, ProductTag.SuccessfulPetition);
  const shouldShowStandardProgressBar =
    (!isEvergreen || (isHybrid && stage === ProductStage.ComingSoon)) && !shouldHideProgressBarForProducts(product.id);
  const isSizedApparel = isSizedApparelProduct(product);

  const { cycPromoMessage } = parseCreatorCollectionMetafields(creatorCollection?.node.metafields);

  // GT June 2025
  const isGorillaTag = isGorillaTagDropCollection(latestProductDropCollection?.id);

  const { state } = useStore();
  const completeTheCollectionRef = useRef<HTMLDivElement | null>(null);
  const redirectBlockRef = useRef<HTMLDivElement | null>(null);
  const [errors, setErrors] = useState<VariantSelectionError[]>([]);

  const { productDropSalesQuantity } = useFetchSalesQuantityForProductDrop(
    latestProductDropCollection,
    currentProductDropSalesQuantity,
  );
  const productDropMOQ = calculateCombinedMOQ(latestProductDropCollection);

  const shouldDisplayStretchGoals =
    shouldEnableStretchGoals &&
    latestProductDropCollection !== null &&
    productDropCollectionMetafields !== null &&
    areAllProductDropMetafieldsFilled(productDropCollectionMetafields) &&
    productDropSalesQuantity !== null &&
    productDropMOQ !== null;

  const isHideUnits = hasProductTag(product.tags, ProductTag.HideUnits);

  const shouldShowLowStock = shouldShowLowStockMessage(metafields.lowStockThreshold, totalInventoryNumber);

  const isStandardVisible =
    ((stage === ProductStage.Live && product.availableForSale) || stage === ProductStage.Draft) && !isSoldOut;

  const isHybridVisible = isHybrid && isHybridSoldOut(product);

  const onClickSizeGuide = () => {
    setIsSizeGuideOpen(true);
    posthog.capture('size_guide_opened', {
      product_id: getProductIDFromShopifyGID(product?.id ?? ''),
      product_title: product?.title,
      product_type: product?.productType,
      is_logged_in: isUserLoggedIn(state.user),
      productStage: getStage(product?.tags ?? []),
    });
  };

  useEffect(() => {
    const clearUpdateFundedBar = updateFundedBar(stage, product, totalInventoryNumber, setTotalInventory);

    return () => {
      clearUpdateFundedBar();
    };
  }, [product]);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        // Determine the sort order based on the product stage
        const sortOrder = getStage(product.tags) === ProductStage.Live ? SortOrder.Descending : SortOrder.Ascending;

        // Fetch the initial badges
        const { data } = await getBadgesByExternalProductId({
          externalProductId: decryptShopifyId(product.id).toString(),
          direction: sortOrder,
          offset: '0',
          limit: '3',
        });

        // Update the state with the fetched badges
        setBadges(data);
      } catch (error) {
        // Handle errors (e.g., update the state, show a message to the user)
        console.error('Failed to fetch initial badges:', error);
      }
    };

    const fetchProductSalesQuantity = async () => {
      if (stage === ProductStage.Past && metafields.totalsold) {
        setTotalInventory(metafields.totalsold);
        return;
      }
      const shopifyProductInventory = Math.abs(product.totalInventory ?? 0);
      const totalInventory = await calculateTotalInventorySold(product);

      setTotalInventory(Number(totalInventory) ?? Number(shopifyProductInventory));
    };

    setBadges([]);
    setMOQ(Number(metafields.moq) || Number(config.defaultMOQ));
    fetchBadges();
    fetchProductSalesQuantity();

    if (!isSizedApparel) {
      setSelectedVariant(product.variants.edges[0].node);
    } else {
      setSelectedVariant(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    if (selectedVariant) {
      const filteredImages = product.images.edges.filter(({ node }) =>
        hasManyVariants
          ? node.altText?.includes(selectedVariant?.title) || !node.altText
          : node.altText === selectedVariant.image?.altText,
      );
      setProductImages(removeFirstProductImage(filteredImages, product, stage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant]);

  const renderProgressBar = (): JSX.Element | null => {
    // GT June 2025
    if (isGorillaTag && shouldDisplayStretchGoals && productDropSalesQuantity < productDropMOQ) {
      // If the Gorilla Tag product is not yet funded, render standard progress bar
      return <ProgressBarContainer fundedBarRef={fundedBarRef} isHideUnits={isHideUnits} />;
    }

    if (shouldDisplayStretchGoals) {
      return (
        <StretchGoalsProgressBarWrapper>
          <StretchGoalsProgressBar
            latestProductDropCollection={latestProductDropCollection}
            productDropCollectionMetafields={productDropCollectionMetafields}
            totalUnitsSold={productDropSalesQuantity}
            productDropMOQ={productDropMOQ}
            isHideUnits={isHideUnits}
          />
        </StretchGoalsProgressBarWrapper>
      );
    }
    if (shouldShowStandardProgressBar) {
      return <ProgressBarContainer fundedBarRef={fundedBarRef} isHideUnits={isHideUnits} />;
    }

    return null;
  };

  return (
    <ProductInfoWrapper>
      {!isEvergreen && (
        <ProductHeaderWrapper data-testid="desktop-product-header">
          <ProductHeader selectedVariant={selectedVariant} creatorCollection={creatorCollection} />
        </ProductHeaderWrapper>
      )}
      <ProductDetails selectedVariant={selectedVariant} creatorCollectionHandle={creatorCollectionHandle} />
      {stage !== ProductStage.Demo && (
        <>
          {renderProgressBar()}
          {stage === ProductStage.Past && (
            <PostPurchaseDetailsWrapper>
              {renderShippingDate(metafields.delivery, stage, false, product, false, state.user, selectedVariant)}
              {isSizedApparel && !isHybrid && <SizeGuide onClick={() => onClickSizeGuide()}>Size Guide</SizeGuide>}
              <SizeGuideModal
                productType={getProductType(product?.productType || '')}
                isOpen={isSizeGuideOpen}
                closeModal={() => setIsSizeGuideOpen(false)}
                product={product}
              />
            </PostPurchaseDetailsWrapper>
          )}
          <PromoBanner />
          {stage === ProductStage.Failed && <RefundBlock />}
          {isEvergreen && !isHybrid && <EvergreenBanner />}
          {(isStandardVisible || isHybridVisible) && (
            <AddToCartContainer
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
              completeTheCollectionRef={completeTheCollectionRef}
              errors={errors}
              setErrors={setErrors}
            />
          )}
          {(stage === ProductStage.ComingSoon || (isSoldOut && !isHybrid)) && (
            <>
              <ButtonPrimary
                className="w-full"
                onClick={() => {
                  setIsModalOpen(true);
                  trackSubscribeButtonClicked(
                    product,
                    state.user,
                    getSubscribeButtonText(stage, isEvergreen),
                    SubscribeModalLocation.PDP,
                  );
                }}
              >
                {getSubscribeButtonText(stage, isEvergreen)}
              </ButtonPrimary>
              {pinAddOnProduct && (
                <PinAddOnBanner>
                  <GiftIcon>🎁</GiftIcon>
                  <S3>
                    Stay tuned for an exclusive enamel pin from{' '}
                    {metafields.creator ? metafields.creator : 'the creator'} 👀
                  </S3>
                </PinAddOnBanner>
              )}
            </>
          )}
          {shouldShowLowStock && <LowStockMessage>Only {totalInventoryNumber} left in stock!</LowStockMessage>}
          {shouldRenderShippingInfo() &&
            renderShippingDate(
              metafields.delivery,
              stage,
              false,
              product,
              isProductHybridPreOrder,
              state.user,
              selectedVariant,
            )}
          {/* topCampaigns is only fetched when stage is Past or Failed. */}
          {(stage === ProductStage.Past || stage === ProductStage.Failed) && topCampaigns.length > 0 && (
            <>
              <LiveProductRedirectFloating
                metafields={metafields}
                productsBySameCreator={productsBySameCreator}
                productsInSameMakeshiftCollection={productsInSameMakeshiftCollection}
                product={product}
                makeshiftCollectionTitle={makeshiftCollectionTitle}
                topCampaigns={topCampaigns}
                redirectBlockRef={redirectBlockRef}
              />
              <LiveProductRedirect
                metafields={metafields}
                productsBySameCreator={productsBySameCreator}
                productsInSameMakeshiftCollection={productsInSameMakeshiftCollection}
                product={product}
                makeshiftCollectionTitle={makeshiftCollectionTitle}
                topCampaigns={topCampaigns}
                redirectBlockRef={redirectBlockRef}
              />
              {pinAddOnProduct && (
                <PinAddOnBanner>
                  <ImageWrapper>
                    <Image
                      src={pinAddOnProduct.images.edges[0].node.transformedSrc}
                      alt={pinAddOnProduct.title}
                      layout="fill"
                      objectFit="contain"
                      quality={90}
                      unoptimized
                    />
                  </ImageWrapper>
                  <S3>This Plushie was part of an exclusive offer with a matching enamel pin! ✨</S3>
                </PinAddOnBanner>
              )}
            </>
          )}
          {isGorillaTag && stage === ProductStage.Past && (
            <GTCodeRedemptionContainer>
              <P2>
                📌 <strong>Redeeming Unlocked Gorilla Tag Rewards</strong>
              </P2>
              <P2>
                One of each unlocked reward per unit purchased. Physical Rewards will be shipped along with your
                product. In-game Reward Codes will be sent to your email by 3PM EST on July 24th, 2025.
              </P2>
            </GTCodeRedemptionContainer>
          )}
          {(shouldDisplayStretchGoals ||
            (productDropCollectionMetafields !== null &&
              latestProductDropCollection !== null &&
              areAllProductDropMetafieldsFilled(productDropCollectionMetafields) &&
              productDropSalesQuantity !== null &&
              productDropMOQ !== null &&
              stage === ProductStage.Past)) && (
            <StretchGoalWrapper>
              <StretchGoalsRewardList
                productDropCollectionMetafields={productDropCollectionMetafields}
                latestProductDropCollection={latestProductDropCollection}
                totalUnitsSold={productDropSalesQuantity}
                productDropMOQ={productDropMOQ}
                isHideUnits={isHideUnits}
                isGorillaTag={isGorillaTag}
              />
            </StretchGoalWrapper>
          )}
          {getIsCompleteTheCollectionEligible(
            productsBySameCreator,
            productsInSameMakeshiftCollection,
            hasProductionDesign,
            stage,
            isSoldOut,
            product,
            pinAddOnProduct,
          ) && (
            <CompleteCollectionComponent
              metafields={metafields}
              productsBySameCreator={productsBySameCreator}
              productsInSameMakeshiftCollection={productsInSameMakeshiftCollection}
              product={product}
              stage={stage}
              makeshiftCollectionTitle={makeshiftCollectionTitle}
              completeTheCollectionRef={completeTheCollectionRef}
              pinAddOnProduct={pinAddOnProduct}
              cycPromoMessage={cycPromoMessage}
            />
          )}
          {(stage === ProductStage.Live || stage === ProductStage.Draft) && giftProduct && (
            <GiftingComponent stage={stage} giftProduct={giftProduct} />
          )}
        </>
      )}
      <>
        {isPetitionCampaign && <PastPetitionMessage />}
        <CreatorMessage />
        {productsBySameCreator.length < 1 &&
          productsInSameMakeshiftCollection.length < 1 &&
          (stage === ProductStage.Past || stage === ProductStage.Failed) &&
          !isHybrid && (
            <ButtonPrimary
              className="w-full"
              onClick={() => {
                setIsModalOpen(true);
                trackSubscribeButtonClicked(
                  product,
                  state.user,
                  getSubscribeButtonText(stage, isEvergreen),
                  SubscribeModalLocation.PDP,
                );
              }}
            >
              {getSubscribeButtonText(stage, isEvergreen)}
            </ButtonPrimary>
          )}
        {stage !== ProductStage.ComingSoon && <PDPInfoDrawer badges={badges} />}
      </>
      <LazySubscribeModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        tags={`${
          isEvergreen
            ? createEvergreenTags(product)
            : createTags(product, creatorCollectionHandle, totalInventoryNumber, moqNumber, isSoldOut)
        }, pdp-${getStage(product.tags)}-campaign-subscribe`}
        description={getSubscribeDescription(stage, isEvergreen, product.title, metafields.creator)}
        title={getSubscribeTitle(stage, isEvergreen)}
        ctaText={getSubscribeTitle(stage, isEvergreen)}
        eventLocation={SubscribeModalLocation.PDP}
      />
    </ProductInfoWrapper>
  );
};

export default ProductInfo;
